"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useEffect,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

type ModalContent = {
  modalId?: string;
  title?: string;
  description?: string;
  body?: ReactNode;
  footer?: ReactNode;
  handleClose?: () => void;
  classNameContent?: string;
};

type ModalContextType = {
  openModal: (content: ModalContent) => void;
  closeModal: (modalId?: string) => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useGlobalModal = () => {
  const context = useContext(ModalContext);
  if (!context)
    throw new Error("useGlobalModal must be used within ModalProvider");
  return context;
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ModalContent>({
    classNameContent: "",
  });

  const handleCloseRef = useRef<(() => void) | undefined>(undefined);
  const modalIdRef = useRef<string | undefined>(undefined);

  const openModal = (content: ModalContent) => {
    setModalContent(content);
    handleCloseRef.current = content.handleClose;
    modalIdRef.current = content.modalId;
    setIsOpen(true);
  };

  const closeModal = (modalId?: string) => {
    if (!modalId || modalId === modalIdRef.current) {
      setIsOpen(false);
      setModalContent({ classNameContent: "" });
      handleCloseRef.current = undefined;
      modalIdRef.current = undefined;
    }
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            if (modalContent?.handleClose) {
              modalContent.handleClose();
              closeModal();
            } else {
              closeModal();
            }
          } else {
            setIsOpen(true);
          }
        }}
      >
        <DialogContent
          className={modalContent?.classNameContent}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DialogHeader className="flex flex-col items-center">
            {modalContent.title && (
              <DialogTitle className="text-2xl font-bold text-[#1C125F]">
                {modalContent.title}
              </DialogTitle>
            )}
            {modalContent.description && (
              <DialogDescription className="text-center text-sm font-normal text-gray-500">
                {modalContent.description}
              </DialogDescription>
            )}
          </DialogHeader>

          {modalContent.body && (
            <div className="py-2 w-full">{modalContent.body}</div>
          )}

          {modalContent.footer && (
            <div className="pt-4">{modalContent.footer}</div>
          )}
        </DialogContent>
      </Dialog>
    </ModalContext.Provider>
  );
};
